/* global React, Icon, usePaged, Pagination, useState, useMemo */
// ============================================================
// PLUMME 2.0 — Shared DataList (table) component
// columns: [{ header, headerRight, thClass, cellClass(string|fn), tdStyle(obj|fn), render(row),
//            sort(row) -> comparable value (optional), sortable:false to opt a column out }]
// Paging is built in: pass the FULL list as `rows`; DataList slices + renders the pager.
// Pass paginate={false} to render every row with no pager.
// Headers are sortable by default (asc → desc → off). Pass sortable={false} to disable for the whole table.
// footer(visibleRows) -> optional <tr> rendered after the body; receives only the rows currently shown
// (current page after sorting) so totals reflect what's visible.
// ============================================================

// Pull plain text out of a rendered React node so any column is sortable without an explicit accessor.
function nodeText(n) {
  if (n == null || n === false || n === true) return '';
  if (typeof n === 'string' || typeof n === 'number') return String(n);
  if (Array.isArray(n)) return n.map(nodeText).join(' ');
  if (n.props) return nodeText(n.props.children);
  return '';
}
// Parse a Norwegian-formatted number ("12 500,00 kr", "45 %") → Number, else null.
function dlNum(v) {
  const s = String(v).trim().replace(/\s/g, '').replace(/kr$/i, '').replace(/%$/, '').replace(/\./g, '').replace(',', '.');
  return /^-?\d+(\.\d+)?$/.test(s) ? parseFloat(s) : null;
}
function dlSortVal(col, row) { return col.sort ? col.sort(row) : nodeText(col.render(row)); }
function dlCompare(a, b) {
  const na = dlNum(a), nb = dlNum(b);
  if (na != null && nb != null) return na - nb;
  return String(a).localeCompare(String(b), 'nb', { numeric: true, sensitivity: 'base' });
}

// ---- Export (CSV / Excel) ----
// columns: [{ header, value(row) }] — value() returns plain text/number; falls back to nodeText(render(row)).
function dlDownload(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
}
function dlCell(col, row) { return col.value ? col.value(row) : nodeText(col.render(row)); }
function exportTable(filename, columns, rows, kind) {
  const headers = columns.map((c) => (typeof c.header === 'string' ? c.header : ''));
  if (kind === 'csv') {
    const SEP = ';'; // Norwegian Excel locale delimiter
    const esc = (v) => { const s = v == null ? '' : String(v); return /[";\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const lines = [headers.map(esc).join(SEP)];
    rows.forEach((r) => lines.push(columns.map((c) => esc(dlCell(c, r))).join(SEP)));
    dlDownload(new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }), filename + '.csv');
  } else {
    const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    let html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1"><thead><tr>' + headers.map((h) => '<th>' + esc(h) + '</th>').join('') + '</tr></thead><tbody>';
    rows.forEach((r) => { html += '<tr>' + columns.map((c) => '<td>' + esc(dlCell(c, r)) + '</td>').join('') + '</tr>'; });
    html += '</tbody></table></body></html>';
    dlDownload(new Blob(['\uFEFF' + html], { type: 'application/vnd.ms-excel' }), filename + '.xls');
  }
}

function DataList({ tableClass = '', columns, rows, rowKey, rowClass, onRowClick, expandedKey, renderExpanded, paginate = true, pageSize = 10, sortable = true, footer, paginationAside }) {
  const [sort, setSort] = useState(null); // { i, dir: 1 | -1 }
  const canSort = (c) => sortable && c.sortable !== false;
  const toggleSort = (i) => {
    if (!canSort(columns[i])) return;
    setSort((s) => (s && s.i === i) ? (s.dir === 1 ? { i, dir: -1 } : null) : { i, dir: 1 });
  };
  const display = useMemo(() => {
    if (!sort) return rows;
    const col = columns[sort.i];
    // Each sort value is computed once per row, not twice per comparison: without an
    // explicit `sort` accessor that value means rendering the cell and walking its text.
    return rows.map((row) => ({ row, value: dlSortVal(col, row) }))
      .sort((a, b) => dlCompare(a.value, b.value) * sort.dir)
      .map((entry) => entry.row);
  }, [rows, sort, columns]);
  const pg = usePaged(display, pageSize);
  const data = paginate ? pg.slice : display;
  return <div className="data-list">
    <div className="tbl-wrap"><table className={'tbl' + (tableClass ? ' ' + tableClass : '')}>
      <thead><tr>{columns.map((c, i) => {
        const on = sort && sort.i === i;
        return <th key={i} className={c.thClass || undefined} style={c.headerRight ? { textAlign: 'right' } : undefined}>
          {canSort(c)
            ? <button type="button" className={'th-sort' + (on ? ' on' : '')} onClick={() => toggleSort(i)} aria-label={'Sorter etter ' + (typeof c.header === 'string' ? c.header : '')}>
                <span>{c.header}</span>
                <span className="th-arrow">{on ? (sort.dir === 1 ? '↑' : '↓') : '↕'}</span>
              </button>
            : c.header}
        </th>;
      })}</tr></thead>
      <tbody>{data.map((row) => {
        const key = rowKey(row);
        const expanded = expandedKey != null && key === expandedKey;
        return <React.Fragment key={key}>
          <tr data-app-control="true" className={typeof rowClass === 'function' ? rowClass(row) : rowClass || undefined} onClick={onRowClick ? (event) => onRowClick(row, event) : undefined} style={onRowClick ? { cursor: 'pointer' } : undefined}>
            {columns.map((c, i) => <td key={i} data-label={typeof c.header === 'string' ? c.header : undefined} className={(typeof c.cellClass === 'function' ? c.cellClass(row) : c.cellClass) || undefined} style={typeof c.tdStyle === 'function' ? c.tdStyle(row) : c.tdStyle}>{c.render(row)}</td>)}
          </tr>
          {expanded && renderExpanded && <tr><td colSpan={columns.length} style={{ background: 'var(--panel)' }}>{renderExpanded(row)}</td></tr>}
        </React.Fragment>;
      })}{footer && footer(data)}</tbody>
    </table></div>
    {paginate && pg.total > 0 && <div className={paginationAside ? 'data-list-pagination mt-16' : 'mt-16'}>
      {paginationAside && <div className="data-list-pagination-aside">{paginationAside}</div>}
      <Pagination {...pg} onPage={pg.setPage} pageSize={pg.ps} onPageSize={pg.setPs} />
    </div>}
  </div>;
}
Object.assign(window, { DataList, exportTable, nodeText });
